import { LoggedInUser } from '@/entities/auth/loggedInUser';
import * as stripeService from '@/_legacy/services/stripe';
import { deleteInvoice } from '@/services/api/resources/subscription/actions';
import { StripeResponse, Subscription } from '@/entities/subscription/model';
import { PlanNickName } from '@/entities/plan/constants';
import { StripeDeclineCodes, SubscriptionStatus } from '@/entities/subscription/constants';
import { asynchronousAuthorization } from '@/ui/subscription/subscription';
import { user as loggedUser } from '@/ui/auth/auth';
import SetupIntentStatus = stripe.setupIntents.SetupIntentStatus;
import PaymentIntentStatus = stripe.paymentIntents.PaymentIntentStatus;

export function planTypeConversion(type: PlanNickName): PlanNickName {
  const map: {[key in PlanNickName]: string} = {
    [PlanNickName.Yearly]: 'year',
    [PlanNickName.Monthly]: 'month',
    [PlanNickName.Month]: 'monthly',
    [PlanNickName.Year]: 'yearly',
    [PlanNickName.Daily]: 'day',
  };

  return map[type] as PlanNickName;
}
export const userPaysByInvoiceLegacy = (user: LoggedInUser) => user._legacy_pays_by_invoice;
export const userPaysByInvoice = () => {
  return loggedUser.value.organization.paysByInvoice;
};
export const isActiveOrTrialing = (status: SubscriptionStatus, pendingSetupIntent: string | Record<string, unknown> | null) => (
  (status === SubscriptionStatus.Active || status === SubscriptionStatus.Trialing)
  && pendingSetupIntent === null
);
export const subscriptionRequiresPaymentMethod = (
  { subscriptionStatus, paymentIntentStatus }:
  { subscriptionStatus: SubscriptionStatus; paymentIntentStatus: PaymentIntentStatus },
) => (
  [SubscriptionStatus.Incomplete, SubscriptionStatus.PastDue, SubscriptionStatus.Active].includes(subscriptionStatus)
  && paymentIntentStatus === 'requires_payment_method'
);

export const subscriptionRequiresAction = (
  { subscriptionStatus, paymentIntentStatus }:
  { subscriptionStatus: SubscriptionStatus; paymentIntentStatus: PaymentIntentStatus },
) => (
  [SubscriptionStatus.Incomplete, SubscriptionStatus.PastDue, SubscriptionStatus.Active].includes(subscriptionStatus)
  && paymentIntentStatus === 'requires_action'
);

export const confirmCardPayment = async (subscription: StripeResponse) => {
  const response = await stripeService.confirmCardPayment(subscription.latestInvoice.paymentIntent!.clientSecret);

  if (!response.error) {
    asynchronousAuthorization.value = true;
  }

  if (response.error && subscription.pendingUpdate) {
    await deleteInvoice({
      subscriptionId: subscription.id,
      invoiceId: subscription.latestInvoice.id,
    })();
  }

  return response;
};

export const confirmCardSetup = async (subscription: StripeResponse) => {
  // Check setup intent and handle next action
  const { pendingSetupIntent, pendingUpdate } = subscription;
  // @ts-ignore
  const {
    clientSecret,
    status,
  }: {
    clientSecret: string;
    status: SetupIntentStatus;
  } = pendingSetupIntent;
  let response: {error?: stripe.Error} = {};

  if (status === 'requires_action') {
    response = await stripeService.confirmCardSetup(clientSecret);

    if (!response.error) {
      asynchronousAuthorization.value = true;
    }

    if (response.error && pendingUpdate) {
      await deleteInvoice({
        subscriptionId: subscription.id,
        invoiceId: subscription.latestInvoice.id,
      })();
    }
  }

  if (status === 'requires_payment_method' && pendingUpdate) {
    await deleteInvoice({
      subscriptionId: subscription.id,
      invoiceId: subscription.latestInvoice.id,
    })();
  }

  return response;
};

export const declineCodeExplanation = (code: keyof typeof StripeDeclineCodes) => {
  const message = StripeDeclineCodes[code];

  return message ?? 'Card declined';
};

export const isIncompleteWithFailure = (subscription: Subscription | null) => subscription
  && subscription.status === SubscriptionStatus.Incomplete
  && subscription.failure;

export const isActiveOrTrailing = (subscription: Subscription | null) => subscription
  && (
    subscription.status === SubscriptionStatus.Active
    || subscription.status === SubscriptionStatus.Trialing
  );

export const hasNoChanges = (
  previousSubscriptionState: Subscription | null,
  currentSubscriptionState: Subscription | null,
) => previousSubscriptionState
  && currentSubscriptionState
  && previousSubscriptionState.status === currentSubscriptionState.status
  && !currentSubscriptionState.failure;
